import React from "react";
import { User, onAuthStateChanged } from "firebase/auth";

import { api } from "api";
import { auth } from "configs";
import { useToast } from "hooks/use-toast";
import { Timestamp } from "firebase/firestore";
import { useAppDispatch, useAppSelector } from "store";
import { selectAdmin, setCurrentAdmin } from "store/admin";

export const useCurrentAdmin = () => {
  const { handleFirebaseError } = useToast();
  const dispatch = useAppDispatch();
  const currentAdmin = useAppSelector(selectAdmin);

  const [loading, setLoading] = React.useState(true);

  const handleAuthStateChanged = React.useCallback(
    async (user: User | null) => {
      try {
        setLoading(true);

        const isCurrentAdmin =
          currentAdmin && user && user.uid === currentAdmin.id;

        if (isCurrentAdmin) {
          return;
        }

        if (!user) {
          dispatch(setCurrentAdmin(undefined));
          return;
        }

        if (user) {
          const admin = await api.admin.get(user.uid);

          if (!admin) {
            dispatch(setCurrentAdmin(undefined));
            await api.admin.logout();
            handleFirebaseError(
              new Error("Permissions denied. Contact Super Admin")
            );

            return;
          }

          const lastSessionAt = Timestamp.now();
          await api.admin.update(admin.id, { lastSessionAt });
          dispatch(setCurrentAdmin({ ...admin, lastSessionAt }));
        }
      } catch (error) {
        handleFirebaseError(error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentAdmin]
  );

  React.useEffect(() => {
    const subscribe = onAuthStateChanged(auth, handleAuthStateChanged);
    return () => subscribe();
  }, [handleAuthStateChanged]);

  return { loading, currentAdmin } as const;
};
